import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../../utils/apiUtils';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fadeState, setFadeState] = useState('visible'); // 'visible' or 'fading'
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  // Filter state
  const [filterTitle, setFilterTitle] = useState('all');
  const [filterRead, setFilterRead] = useState('all');
  const [availableTitles, setAvailableTitles] = useState([]);

  // Fetch available notification titles for filter dropdown
  useEffect(() => {
    const fetchTitles = async () => {
      try {
        const response = await apiCall('/api/notifications/titles');
        setAvailableTitles(response.titles || []);
      } catch (error) {
        console.error('Error fetching notification titles:', error);
      }
    };
    fetchTitles();
  }, []);

  const fetchNotifications = useCallback(async (page = 1, title = filterTitle, read = filterRead, isInitial = false) => {
    // Start fade out (skip on initial load)
    if (!isInitial) {
      setFadeState('fading');
      await new Promise(resolve => setTimeout(resolve, 150)); // Wait for fade out
    }

    setLoading(true);
    try {
      let url = `/api/notifications?page=${page}&limit=20`;
      if (title !== 'all') {
        url += `&title=${encodeURIComponent(title)}`;
      }
      if (read !== 'all') {
        url += `&read=${read === 'read'}`;
      }
      const response = await apiCall(url);
      setNotifications(response.notifications);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
      // Fade back in after a tiny delay
      setTimeout(() => setFadeState('visible'), 50);
    }
  }, [filterTitle, filterRead]);

  useEffect(() => {
    fetchNotifications(1, filterTitle, filterRead, true); // isInitial=true for first load
  }, [filterTitle, filterRead, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await apiCall(`/api/notifications/${notificationId}/read`, {
        method: 'POST',
      });
      fetchNotifications(pagination.page, filterTitle, filterRead);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiCall('/api/notifications/mark-all-read', {
        method: 'POST',
      });
      fetchNotifications(1, filterTitle, filterRead);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchNotifications(newPage, filterTitle, filterRead);
    }
  };

  const handleFilterTitleChange = (title) => {
    setFilterTitle(title);
  };

  const handleFilterReadChange = (read) => {
    setFilterRead(read);
  };

  const getNotificationIcon = (type) => {
    const icons = {
      success: '✅',
      info: 'ℹ️',
      warning: '⚠️',
      error: '❌'
    };
    return icons[type] || 'ℹ️';
  };

  const getNotificationColor = (type) => {
    const colors = {
      success: 'bg-green-50 border-green-200',
      info: 'bg-blue-50 border-blue-200',
      warning: 'bg-yellow-50 border-yellow-200',
      error: 'bg-red-50 border-red-200'
    };
    return colors[type] || 'bg-gray-50 border-gray-200';
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const totalPages = pagination.total_pages;
    const currentPage = pagination.page;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  // Skeleton loading component
  const SkeletonLoader = () => (
    <div className="space-y-4">
      {[...Array(6)].map((_, index) => (
        <div key={index} className="p-4 rounded-lg border border-gray-200 bg-gray-50 animate-pulse">
          <div className="flex items-start">
            <div className="w-8 h-8 bg-gray-300 rounded-full mr-3 flex-shrink-0"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-300 rounded w-1/3 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3 mb-2"></div>
              <div className="h-2 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const filterReadOptions = [
    { value: 'all', label: 'All' },
    { value: 'unread', label: 'Unread' },
    { value: 'read', label: 'Read' }
  ];

  // Calculate heights: 
  // - Header: ~100px (with filters)
  // - Footer: ~60px
  // - Dashboard nav + padding: ~110px
  // Scrollable area = viewport height - header - footer - nav - padding
  const scrollableHeight = 'calc(100vh - 280px)';

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Fixed Header with Filters */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
            {pagination.total_count > 0 && filterRead !== 'read' && (
              <button
                onClick={markAllAsRead}
                disabled={loading}
                className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50"
              >
                Mark All as Read
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Title/Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 font-medium">Status:</label>
              <select
                value={filterTitle}
                onChange={(e) => handleFilterTitleChange(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[180px]"
              >
                <option value="all">All Notifications</option>
                {availableTitles.map(title => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </select>
            </div>

            {/* Read Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 font-medium">Read:</label>
              <div className="flex rounded-md overflow-hidden border border-gray-300">
                {filterReadOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleFilterReadChange(option.value)}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${filterRead === option.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content - FIXED HEIGHT with overflow scroll and fade transition */}
      <div
        className="overflow-y-auto p-6"
        style={{
          height: scrollableHeight,
          maxHeight: scrollableHeight,
          opacity: fadeState === 'fading' ? 0 : 1,
          transition: 'opacity 300ms ease-in-out'
        }}
      >
        {loading ? (
          <SkeletonLoader />
        ) : notifications.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-gray-400 text-6xl mb-4">🔔</div>
            <p className="text-gray-500 text-lg">No notifications found</p>
            {(filterTitle !== 'all' || filterRead !== 'all') && (
              <button
                onClick={() => { setFilterTitle('all'); setFilterRead('all'); }}
                className="mt-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => !notification.read && markAsRead(notification.id)}
                className={`p-4 rounded-lg border transition-all duration-200 hover:shadow-md ${notification.read ? 'bg-gray-50 border-gray-200' : `${getNotificationColor(notification.type)} cursor-pointer`
                  }`}
                title={!notification.read ? 'Click to mark as read' : ''}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <div className="text-2xl mr-3 flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{notification.title}</h3>
                        {!notification.read && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700 mt-1">{notification.message}</p>
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {new Date(notification.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  {!notification.read && (
                    <span className="text-blue-600 text-xs font-medium flex-shrink-0 ml-4">
                      Click to mark read
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fixed Pagination Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          {/* Left side - Page counter */}
          <div className="text-sm text-gray-600">
            {pagination.total_count > 0 ? (
              <span>
                Showing <span className="font-semibold">{((pagination.page - 1) * pagination.limit) + 1}</span> - <span className="font-semibold">{Math.min(pagination.page * pagination.limit, pagination.total_count)}</span> of <span className="font-semibold">{pagination.total_count}</span> notifications
              </span>
            ) : (
              <span>No notifications</span>
            )}
          </div>

          {/* Right side - Navigation buttons */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center space-x-2">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.has_prev || loading}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${pagination.has_prev && !loading
                    ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                  }`}
              >
                ← Prev
              </button>

              {/* Page Numbers */}
              <div className="hidden sm:flex items-center space-x-1">
                {getPageNumbers().map((page, index) => (
                  <React.Fragment key={index}>
                    {page === '...' ? (
                      <span className="px-2 text-gray-500">...</span>
                    ) : (
                      <button
                        onClick={() => handlePageChange(page)}
                        disabled={loading}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${page === pagination.page
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          }`}
                      >
                        {page}
                      </button>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Mobile page indicator */}
              <span className="sm:hidden text-sm text-gray-600">
                Page {pagination.page} of {pagination.total_pages}
              </span>

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.has_next || loading}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${pagination.has_next && !loading
                    ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                  }`}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;